import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDown, Upload, Loader2, Info } from "lucide-react";
import { toast } from "sonner";

const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

async function downloadBlobGet(path, params, fallbackFilename) {
  const response = await api.get(path, {
    responseType: "blob",
    params,
    validateStatus: () => true,
  });
  const { data, headers, status } = response;
  const ctype = String(headers["content-type"] || headers["Content-Type"] || "");
  if (status >= 400 || ctype.includes("json")) {
    let detail = "";
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text());
        detail = formatApiError(parsed.detail) || parsed.detail?.message || "";
      } catch {
        detail = "";
      }
    }
    throw new Error(detail || `Error ${status} al descargar plantilla`);
  }
  if (!(data instanceof Blob) || data.size === 0) {
    throw new Error("Archivo vacío");
  }
  const cd = headers["content-disposition"] || headers["Content-Disposition"] || "";
  let name = fallbackFilename;
  const quoted = cd.match(/filename="([^"]+)"/i);
  if (quoted) name = quoted[1];
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ConciliacionManualExcelPanel({ clientId, year, onImported }) {
  const fileRef = useRef(null);
  const [conciliaAnio, setConciliaAnio] = useState(() => new Date().getFullYear());
  const [conciliaMes, setConciliaMes] = useState(() => String(new Date().getMonth() + 1).padStart(2, "0"));
  const [listadoPeriodo, setListadoPeriodo] = useState("ejercicio");
  const [listadoAnioMes, setListadoAnioMes] = useState(() => year || new Date().getFullYear());
  const [listadoMesNum, setListadoMesNum] = useState(() => String(new Date().getMonth() + 1).padStart(2, "0"));
  const [listadoRangoDesde, setListadoRangoDesde] = useState("");
  const [listadoRangoHasta, setListadoRangoHasta] = useState("");
  const [listadoDia, setListadoDia] = useState("");
  const [soloPorPagar, setSoloPorPagar] = useState(true);
  const [dlBusy, setDlBusy] = useState(false);
  const [upBusy, setUpBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const mesConciliacion = `${conciliaAnio}-${conciliaMes}`;

  const buildListadoParams = () => {
    const params = {};
    if (listadoPeriodo === "mes") {
      params.listado_mes = `${listadoAnioMes}-${listadoMesNum}`;
    } else if (listadoPeriodo === "rango") {
      if (listadoRangoDesde) params.fecha_desde = listadoRangoDesde.slice(0, 10);
      if (listadoRangoHasta) params.fecha_hasta = listadoRangoHasta.slice(0, 10);
    } else if (listadoPeriodo === "dia" && listadoDia) {
      const d = listadoDia.slice(0, 10);
      params.fecha_desde = d;
      params.fecha_hasta = d;
    }
    return params;
  };

  const onDownload = async () => {
    if (!clientId) {
      toast.error("Selecciona un cliente");
      return;
    }
    if (!mesConciliacion || mesConciliacion.length < 7) {
      toast.error("Indica el mes de conciliación (YYYY-MM)");
      return;
    }
    setDlBusy(true);
    try {
      await downloadBlobGet(
        "/excel/modo-complemento/plantilla-conciliacion-xlsx",
        {
          client_id: clientId,
          year,
          mes_conciliacion: mesConciliacion,
          solo_por_pagar: soloPorPagar,
          ...buildListadoParams(),
        },
        `conciliacion_manual_${mesConciliacion}.xlsx`,
      );
      toast.success("Plantilla descargada");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "No se pudo descargar");
    } finally {
      setDlBusy(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !clientId) return;
    setUpBusy(true);
    setLastResult(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("client_id", clientId);
    try {
      const { data } = await api.post("/excel/modo-complemento/importar-conciliacion-xlsx", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setLastResult(data);
      toast.success(data.message || "Conciliación importada");
      onImported?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.errors)) {
        toast.error(detail.errors.slice(0, 3).join(" · ") || "Errores en el Excel");
      } else {
        toast.error(formatApiError(detail) || "Error al importar");
      }
    } finally {
      setUpBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-md border border-sky-200 bg-sky-50/90 px-4 py-3 text-sm text-slate-800 flex gap-3">
        <Info className="w-5 h-5 shrink-0 text-sky-700 mt-0.5" />
        <div className="space-y-1 text-[13px] leading-snug">
          <p>
            Descargue la plantilla con <strong>Ingresos</strong>, <strong>Egresos</strong> y{" "}
            <strong>Nóminas</strong>. El sistema precarga datos del XML (fecha emisión, saldos, nómina).
          </p>
          <p>
            En columnas amarillas: <strong>S/N</strong> cobrado o pagado, fecha (precargada con hoy; bórrela si marca{" "}
            <strong>N</strong>), parcial (hasta <strong>5 abonos</strong>). En nóminas use <strong>P/N</strong> (pagado / no pagado).
          </p>
          <p>
            En cada hoja use los <strong>filtros del encabezado</strong> en Folio o Contraparte/Nombre para localizar filas
            (también <strong>Ctrl+F</strong>).
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        <div>
          <Label className="text-[10px] uppercase text-slate-500">Mes que concilia (cobro/pago) · año</Label>
          <Select
            value={String(conciliaAnio)}
            onValueChange={(v) => setConciliaAnio(Number(v))}
            disabled={!clientId}
          >
            <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Array.from({ length: 18 }, (_, i) => (year || new Date().getFullYear()) - 12 + i).map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-slate-500">Mes que concilia · mes</Label>
          <Select value={conciliaMes} onValueChange={setConciliaMes} disabled={!clientId}>
            <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MESES.map((nombre, i) => {
                const m = String(i + 1).padStart(2, "0");
                return <SelectItem key={m} value={m}>{nombre}</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-slate-500">Listado XML · periodo emisión</Label>
          <Select value={listadoPeriodo} onValueChange={setListadoPeriodo} disabled={!clientId}>
            <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ejercicio">Todo el ejercicio ({year})</SelectItem>
              <SelectItem value="mes">Mes y año (cualquier año)</SelectItem>
              <SelectItem value="rango">Rango de fechas</SelectItem>
              <SelectItem value="dia">Un día</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {listadoPeriodo === "mes" && (
          <>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Emisión · año</Label>
              <Select
                value={String(listadoAnioMes)}
                onValueChange={(v) => setListadoAnioMes(Number(v))}
                disabled={!clientId}
              >
                <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 18 }, (_, i) => (year || new Date().getFullYear()) - 12 + i).map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Emisión · mes</Label>
              <Select value={listadoMesNum} onValueChange={setListadoMesNum} disabled={!clientId}>
                <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MESES.map((nombre, i) => {
                    const m = String(i + 1).padStart(2, "0");
                    return <SelectItem key={m} value={m}>{nombre}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
          </>
        )}
        {listadoPeriodo === "rango" && (
          <>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Emisión · desde</Label>
              <Input
                type="date"
                className="mt-1 h-9 text-xs"
                value={listadoRangoDesde}
                onChange={(e) => setListadoRangoDesde(e.target.value)}
                disabled={!clientId}
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Emisión · hasta</Label>
              <Input
                type="date"
                className="mt-1 h-9 text-xs"
                value={listadoRangoHasta}
                onChange={(e) => setListadoRangoHasta(e.target.value)}
                disabled={!clientId}
              />
            </div>
          </>
        )}
        {listadoPeriodo === "dia" && (
          <div>
            <Label className="text-[10px] uppercase text-slate-500">Emisión · día</Label>
            <Input
              type="date"
              className="mt-1 h-9 text-xs"
              value={listadoDia}
              onChange={(e) => setListadoDia(e.target.value)}
              disabled={!clientId}
            />
          </div>
        )}
        <div className="flex items-end pb-1 sm:col-span-2 lg:col-span-1">
          <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
            <Checkbox
              checked={soloPorPagar}
              onCheckedChange={(v) => setSoloPorPagar(v === true)}
              disabled={!clientId}
            />
            Solo por pagar / pendientes (+ ya guardados)
          </label>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <Button
          type="button"
          className="bg-[#0F2D5C] hover:bg-[#0b2249] h-9 text-xs"
          disabled={dlBusy || !clientId}
          onClick={onDownload}
        >
          {dlBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />}
          Descargar plantilla Excel
        </Button>
        <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={onUpload} />
        <Button
          type="button"
          variant="outline"
          className="h-9 text-xs"
          disabled={upBusy || !clientId}
          onClick={() => fileRef.current?.click()}
        >
          {upBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
          Subir Excel conciliado
        </Button>
      </div>

      {lastResult?.ok && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50/80 p-4 text-xs text-emerald-950 space-y-1">
          <div className="font-semibold">Importación completada · ejercicio {lastResult.ejercicio_ui}</div>
          <div>
            Ingresos: {lastResult.ingresos?.inserted ?? 0} · Egresos: {lastResult.egresos?.inserted ?? 0} · Nóminas:{" "}
            {lastResult.nominas?.inserted ?? 0}
          </div>
          {(lastResult.skipped_sin_marca ?? 0) > 0 && (
            <div className="text-emerald-800">Filas sin S/N o P/N (omitidas): {lastResult.skipped_sin_marca}</div>
          )}
        </div>
      )}
    </div>
  );
}
